import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Heart,
  Activity,
  Thermometer,
  Droplets,
  Scale,
  TestTube,
  Calendar,
  Target,
  ArrowRight
} from 'lucide-react';
import { format } from 'date-fns';
import { useHealthMetrics } from '@/hooks/use-health-metrics';
import { useAppointments } from '@/hooks/use-appointments';
import AIGreetingCard from '@/components/dashboard/AIGreetingCard';
import VitalTrendCard from '@/components/dashboard/VitalTrendCard';
import NudgeFeed from '@/components/dashboard/NudgeFeed';
import MedicationAdherenceWidget from '@/components/dashboard/MedicationAdherenceWidget';
import ScreeningReminders from '@/components/dashboard/ScreeningReminders';
import GlassCard from '@/components/ui/glass-card';
import Loading from '@/components/ui/Loading';
import { cn } from '@/lib/utils';

type VitalStatus = 'normal' | 'elevated' | 'high' | 'low' | 'critical';
type TrendDirection = 'improving' | 'stable' | 'worsening' | 'unknown';

// Helper function to determine vital status based on value
const getVitalStatus = (type: string, value: number | null | undefined): VitalStatus => {
  if (value === null || value === undefined) return 'normal';

  switch (type) {
    case 'heartRate':
      if (value < 50 || value > 120) return 'critical';
      if (value < 60 || value > 100) return 'elevated';
      return 'normal';

    case 'bloodPressure':
      if (value >= 180) return 'critical';
      if (value >= 140) return 'high';
      if (value >= 130) return 'elevated';
      return 'normal';

    case 'temperature':
      if (value < 95 || value > 103) return 'critical';
      if (value < 97 || value > 99.5) return 'elevated';
      return 'normal';

    case 'oxygen':
      if (value < 90) return 'critical';
      if (value < 95) return 'low';
      return 'normal';

    case 'weight':
      return 'normal'; // Would need BMI calculation for proper status

    case 'hba1c':
      if (value >= 8.0) return 'high';
      if (value >= 6.5) return 'elevated';
      return 'normal';

    default:
      return 'normal';
  }
};

// Calculate trend direction by comparing last value to average of previous values
const calculateTrend = (
  values: number[],
  lowerIsBetter: boolean = false
): TrendDirection => {
  if (!values || values.length < 2) return 'unknown';

  const lastValue = values[values.length - 1];
  const previousValues = values.slice(0, -1);
  const average = previousValues.reduce((a, b) => a + b, 0) / previousValues.length;

  const percentChange = ((lastValue - average) / average) * 100;

  if (Math.abs(percentChange) <= 5) return 'stable';

  if (lowerIsBetter) {
    return percentChange < 0 ? 'improving' : 'worsening';
  } else {
    return percentChange > 0 ? 'improving' : 'worsening';
  }
};

// Health goals data (mock - would come from API)
const mockHealthGoals = [
  { id: '1', title: 'Walk 10,000 steps daily', progress: 75, target: '10,000 steps' },
  { id: '2', title: 'Take medications on time', progress: 92, target: '100%' },
  { id: '3', title: 'Log meals 3x daily', progress: 60, target: '3 meals/day' },
];

const Home: React.FC = () => {
  const { healthData, isLoading: isMetricsLoading } = useHealthMetrics();
  const { appointments, isLoading: isAppointmentsLoading } = useAppointments();

  // Extract upcoming appointments (next 3)
  const upcomingAppointments = useMemo(() => {
    if (!appointments) return [];
    const now = new Date();
    return appointments
      .filter(apt => new Date(apt.startTime) >= now)
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime())
      .slice(0, 3);
  }, [appointments]);

  // Prepare vital trend cards data
  const vitalCards = useMemo(() => {
    if (!healthData) return [];

    const extractValues = (data: Array<{ date: string; value: number }> = []) =>
      data.map(point => point.value);

    return [
      {
        label: 'Heart Rate',
        value: healthData.latestHeartRate || 0,
        unit: 'bpm',
        icon: Heart,
        sparklineData: extractValues(healthData.heartRate),
        status: getVitalStatus('heartRate', healthData.latestHeartRate),
        trend: calculateTrend(extractValues(healthData.heartRate), true),
      },
      {
        label: 'Blood Pressure',
        value: healthData.latestBloodPressureSystolic || 0,
        unit: 'mmHg',
        icon: Activity,
        sparklineData: extractValues(healthData.bloodPressureSystolic),
        status: getVitalStatus('bloodPressure', healthData.latestBloodPressureSystolic),
        trend: calculateTrend(extractValues(healthData.bloodPressureSystolic), true),
      },
      {
        label: 'Temperature',
        value: healthData.latestTemperature || 0,
        unit: '°F',
        icon: Thermometer,
        sparklineData: extractValues(healthData.temperature),
        status: getVitalStatus('temperature', healthData.latestTemperature),
        trend: calculateTrend(extractValues(healthData.temperature), true),
      },
      {
        label: 'O₂ Saturation',
        value: healthData.latestOxygenSaturation || 0,
        unit: '%',
        icon: Droplets,
        sparklineData: extractValues(healthData.oxygenSaturation),
        status: getVitalStatus('oxygen', healthData.latestOxygenSaturation),
        trend: calculateTrend(extractValues(healthData.oxygenSaturation), false),
      },
      {
        label: 'Weight',
        value: healthData.latestWeight || 0,
        unit: 'lbs',
        icon: Scale,
        sparklineData: extractValues(healthData.weight),
        status: getVitalStatus('weight', healthData.latestWeight),
        trend: calculateTrend(extractValues(healthData.weight), true),
      },
      {
        label: 'HbA1c',
        value: healthData.latestHemoglobinA1c || 0,
        unit: '%',
        icon: TestTube,
        sparklineData: extractValues(healthData.hemoglobinA1c),
        status: getVitalStatus('hba1c', healthData.latestHemoglobinA1c),
        trend: calculateTrend(extractValues(healthData.hemoglobinA1c), true),
      },
    ].filter(card => card.value > 0); // Only show cards with data
  }, [healthData]);

  const isLoading = isMetricsLoading || isAppointmentsLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loading />
      </div>
    );
  }

  return (
    <div className="px-4 sm:px-6 py-6 space-y-6 max-w-7xl mx-auto">
      {/* AI Greeting Card - Full Width */}
      <AIGreetingCard />

      {/* Health Pulse Strip - Horizontal Scrollable */}
      <section>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
          Health Pulse
        </h2>
        <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-none snap-x snap-mandatory">
          {vitalCards.map((card, index) => (
            <div key={index} className="snap-start shrink-0">
              <VitalTrendCard
                label={card.label}
                value={card.value}
                unit={card.unit}
                trend={card.trend}
                status={card.status}
                sparklineData={card.sparklineData}
                icon={card.icon}
              />
            </div>
          ))}
        </div>
      </section>

      {/* Smart Cards Grid - 2 columns desktop, 1 column mobile */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column */}
        <div className="space-y-6">
          {/* Nudge Feed */}
          <NudgeFeed />

          {/* Health Goals */}
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Target className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                Health Goals
              </h3>
              <Link
                to="/health-hub"
                className="text-sm text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 transition-colors flex items-center gap-1"
              >
                View All
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            <div className="space-y-4">
              {mockHealthGoals.map((goal) => (
                <div key={goal.id} className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="font-medium text-gray-900 dark:text-gray-100">
                      {goal.title}
                    </span>
                    <span className="text-purple-600 dark:text-purple-400 font-semibold">
                      {goal.progress}%
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-purple-500 to-purple-600 h-full rounded-full transition-all duration-500 ease-out"
                      style={{ width: `${goal.progress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">
                    Target: {goal.target}
                  </p>
                </div>
              ))}
            </div>
          </GlassCard>
        </div>

        {/* Right Column */}
        <div className="space-y-6">
          {/* Medication Adherence Widget */}
          <MedicationAdherenceWidget />

          {/* Upcoming Appointments */}
          <GlassCard className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                <Calendar className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                Upcoming Appointments
              </h3>
              <Link
                to="/appointments"
                className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 transition-colors flex items-center gap-1"
              >
                View All
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {upcomingAppointments.length > 0 ? (
              <div className="space-y-3">
                {upcomingAppointments.map((appointment) => (
                  <Link
                    key={appointment.id}
                    to={`/appointments/${appointment.id}`}
                    className="block p-4 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-blue-400 dark:hover:border-blue-500 transition-colors bg-white/50 dark:bg-gray-800/50"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-medium text-gray-900 dark:text-gray-100">
                          {appointment.appointmentType || 'Appointment'}
                        </p>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                          {appointment.providerName || 'Healthcare Provider'}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                          {format(new Date(appointment.startTime), 'MMM d, yyyy • h:mm a')}
                        </p>
                      </div>
                      <div className={cn(
                        "px-2 py-1 rounded-md text-xs font-medium",
                        appointment.status === 'confirmed' && "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400",
                        appointment.status === 'pending' && "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400",
                        appointment.status === 'cancelled' && "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                      )}>
                        {appointment.status || 'scheduled'}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <Calendar className="h-12 w-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" />
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                  No upcoming appointments
                </p>
                <Link
                  to="/appointments/schedule"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  Schedule Appointment
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
          </GlassCard>
        </div>
      </div>

      {/* Bottom Row - Screening Reminders */}
      <ScreeningReminders />
    </div>
  );
};

export default Home;
